import { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import posthog from 'posthog-js';
import { createContext } from 'use-context-selector';

import { FeatureFlagsOptions } from '@/hooks/useFeatureFlags/types';

type FeatureFlagsProps = {
  [key in FeatureFlagsOptions]: boolean;
};

interface IFeatureFlag {
  featureFlags: FeatureFlagsProps;
  loading: boolean;
  setFeatureFlags: Function;
}

export const FeatureFlagContext = createContext<IFeatureFlag>({
  featureFlags: {} as FeatureFlagsProps,
  loading: true,
  setFeatureFlags: () => {},
});

export const FeatureFlagProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [loading, setLoading] = useState(true);

  // Inicializa o estado com todas as flags como `false`
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsProps>(() =>
    Object.values(FeatureFlagsOptions).reduce((acc, key) => {
      return {
        ...acc,
        [key as FeatureFlagsOptions]: false,
      };
    }, {} as FeatureFlagsProps),
  );

  useEffect(() => {
    let cancelled = false;

    const fetchFeatureFlags = () => {
      if (cancelled) return;

      // Solução temporária: Foi acordado com o time em 28/04/2025 a não realizar chamadas ao posthog.getFeatureFlag` para as flags que estão desabilitadas no posthog devido aos custos terem sido elevados pelo consumo das ff.
      // Essa solução é temporária e deve ser revisada quando tivermos uma solução mais robusta para o gerenciamento de feature flags.
      // A solução ideal seria utilizar o `posthog.getFeatureFlag` para todas as flags, mas isso pode aumentar os custos de ff no posthog.
      // O código abaixo força as flags que estão desabilitadas no posthog a serem `false` no front-end, não necessitando chamar a função posthog.getFeatureFlag para essas flags em específico.

      const flagsDisabled: FeatureFlagsOptions[] = [
        FeatureFlagsOptions.FF_SAPRON_OLD_BANNER_SPOT,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_OWNER_EDITION,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_HOST_BANK_ACCOUNT_EDITION,
      ];

      const flagKeys: FeatureFlagsOptions[] = [
        // Lista de feature flags que deseja buscar
        FeatureFlagsOptions.FF_SAPRON_OLD_BANNER_SPOT,
        FeatureFlagsOptions.FF_SAPRON_OWNER_DROPDOWN,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_OWNER_EDITION,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_HOST_BANK_ACCOUNT_EDITION,
        FeatureFlagsOptions.FF_SAPRON_BACKEND_ALLOW_DATA_EDIT,
        FeatureFlagsOptions.FF_MESSAGE_PRE_CHECKIN,
        FeatureFlagsOptions.FF_MESSAGE_CHECKIN,
        FeatureFlagsOptions.FF_MESSAGE_PENDING_CHECKIN,
        FeatureFlagsOptions.FF_MESSAGE_SAME_DAY_PENDING_CHECKIN,
        FeatureFlagsOptions.FF_DISABLE_CHECKIN_CONTROLLER_V2,
        FeatureFlagsOptions.FF_ENABLE_FRANCHISE_NEW_DAMAGE_V2,
        FeatureFlagsOptions.FF_ENABLE_WHATSAPP_DOCUMENTATION_SENT,
        FeatureFlagsOptions.FF_COMP_RESERVATION_VIEW_MODE,
        FeatureFlagsOptions.FF_ENABLE_ONBOARDING_PAYMENT_METHODS,
        FeatureFlagsOptions.FF_ENABLE_POSTHOG_TRACKING_FOR_PARTNERS,
        FeatureFlagsOptions.FF_CONCILIATION_WEBSITE_SEAZONE,
        FeatureFlagsOptions.FF_ENABLE_MARKETPLACE_FORM,
        FeatureFlagsOptions.FF_ENABLE_NEW_INPUT_BUILDER_FORM,
        FeatureFlagsOptions.FF_ENABLE_CREATE_PARTNER,
        FeatureFlagsOptions.FF_ENABLE_BANNER_CARROSEL,
        FeatureFlagsOptions.FF_ENABLE_WHATSAPP_GROUP,
        FeatureFlagsOptions.FF_ENABLE_NEW_COMMISSION_TABLE,
        FeatureFlagsOptions.FF_ENABLE_NEW_RESCUE_FORM,
        FeatureFlagsOptions.FF_ENABLE_RANKING_PARTNER,
        FeatureFlagsOptions.FF_ENABLE_WEB_PUSH_NOTIFICATIONS,
        FeatureFlagsOptions.FF_MGO_LINK,
        FeatureFlagsOptions.FF_ENABLE_SPOT_SALES_MIRROR,
        FeatureFlagsOptions.FF_ENABLE_SPOT_ADJUSTMENTS,
        FeatureFlagsOptions.FF_ENABLE_HOST_HOME_BANNER,
        FeatureFlagsOptions.FF_ENABLE_EDIT_OPERATIONAL_DETAILS,
        FeatureFlagsOptions.FF_ENABLE_HOT_OPPORTUNITIES,
        FeatureFlagsOptions.FF_ENABLE_COMISSION_PARTNER,
        FeatureFlagsOptions.FF_ENABLE_DOC_SELFIE_FIELD,
        FeatureFlagsOptions.FF_ENABLE_GUESTWATCH,
        FeatureFlagsOptions.FF_ENABLE_FRANCHISE_FEE_MONTHLY_CAP,
        FeatureFlagsOptions.FF_ENABLE_SPOTMATCH_PARTNER,
        FeatureFlagsOptions.FF_ENABLE_NEW_LANCAMENTOS,
        FeatureFlagsOptions.FF_CENTRAL_ATIVIDADES_SPN,
        FeatureFlagsOptions.FF_MANUTENCAO,
        FeatureFlagsOptions.FF_MANUTENCAO_AGENTES_SEAZONER,
        FeatureFlagsOptions.FF_REDIRECT_HOST_TO_FRANQUIAS,
        FeatureFlagsOptions.FF_SAPRON_BACKEND_ENABLE_INSURANCE_AUTOMATION,
        FeatureFlagsOptions.FF_ENABLE_REVENDAS_PARTNER,
        FeatureFlagsOptions.FF_HIDE_REVENDAS_APOIO_VENDAS,
        FeatureFlagsOptions.FF_PARTNERS_INFORMATIVO_SPOTSYS,
        FeatureFlagsOptions.FF_CHANNEL_MANAGER_ENABLED,
        FeatureFlagsOptions.FF_SAPRON_BACKEND_ENABLE_ATTENDANT_HOST_PASSWORD_RESET,
        FeatureFlagsOptions.FF_ENABLE_PARTNER_PAYMENT_FLOW,
        FeatureFlagsOptions.FF_SZS_JORNADA_UNIFICADA_ENABLED,
        FeatureFlagsOptions.FF_WALLET_GRANDES_OPERACOES,
        FeatureFlagsOptions.FF_ENABLE_ESPELHO_NATIVO,
        FeatureFlagsOptions.FF_PARCEIRO_FECHAR,
        FeatureFlagsOptions.FF_REDIRECT_HOSTZONE_INDICACOES,
        FeatureFlagsOptions.FF_REDIRECT_HOSTZONE_SALDO,
        FeatureFlagsOptions.FF_PARTNERS_TICKET_MEDIO_COM_CHAMADAS,
      ];

      // Overrides locais por env, num mapa em vez de uma cadeia de `if`.
      //
      // ORDEM IMPORTA: isto é consultado ANTES de `flagsDisabled`. Antes o
      // override vinha depois, e como as flags em desenvolvimento estão
      // justamente na lista de desabilitadas, todo bloco de override era
      // CÓDIGO MORTO — a flag voltava `false` e nada na tela explicava por quê.
      //
      // Em produção nenhuma destas vars existe, então o mapa é inerte e a
      // decisão continua vindo do PostHog.
      const overridesLocais: Partial<Record<FeatureFlagsOptions, string | undefined>> = {
        [FeatureFlagsOptions.FF_ENABLE_POSTHOG_TRACKING_FOR_PARTNERS]: import.meta.env
          .REACT_APP_FF_ENABLE_POSTHOG_TRACKING_FOR_PARTNERS,
        [FeatureFlagsOptions.FF_ENABLE_NEW_LANCAMENTOS]: import.meta.env
          .REACT_APP_FF_ENABLE_NEW_LANCAMENTOS,
        [FeatureFlagsOptions.FF_ENABLE_REVENDAS_PARTNER]: import.meta.env
          .REACT_APP_FF_ENABLE_REVENDAS_PARTNER,
        [FeatureFlagsOptions.FF_HIDE_REVENDAS_APOIO_VENDAS]: import.meta.env
          .REACT_APP_FF_HIDE_REVENDAS_APOIO_VENDAS,
        [FeatureFlagsOptions.FF_HIDE_REVENDAS_DECOR]: import.meta.env
          .REACT_APP_FF_HIDE_REVENDAS_DECOR,
        // Virada das seções de obra para o Informativo do SpotSys. Em stg/prd a
        // var não é injetada e quem decide é o PostHog (projeto do Sapron).
        [FeatureFlagsOptions.FF_PARTNERS_INFORMATIVO_SPOTSYS]: import.meta.env
          .REACT_APP_FF_PARTNERS_INFORMATIVO_SPOTSYS,
        [FeatureFlagsOptions.FF_ENABLE_PARTNER_PAYMENT_FLOW]: import.meta.env
          .REACT_APP_FF_ENABLE_PARTNER_PAYMENT_FLOW,
        // A flag que substituiu a de cima. Sem esta entrada, a jornada unificada
        // ficaria impossível de ligar em qualquer lugar: ela está em
        // `flagsDisabled`, então o PostHog nunca é consultado para ela.
        [FeatureFlagsOptions.FF_SZS_JORNADA_UNIFICADA_ENABLED]: import.meta.env
          .REACT_APP_FF_SZS_JORNADA_UNIFICADA_ENABLED,
        // Sem esta, o formulário de indicação de SPOT fica IMPOSSÍVEL de
        // completar em desenvolvimento: o schema exige `nacionalidade` e
        // `profissao` sempre que `investor_type` é 'PF' (o default), mas os
        // inputs dos dois só renderizam com esta flag ligada.
        [FeatureFlagsOptions.FF_ENABLE_SPOT_ADJUSTMENTS]: import.meta.env
          .REACT_APP_FF_ENABLE_SPOT_ADJUSTMENTS,
        // Está em `flagKeys` e não estava aqui: sem key do PostHog — o caso do
        // dev local — o espelho de vendas era INALCANÇÁVEL, porque a rota
        // redireciona para o painel antes de renderizar quando ela é falsa.
        [FeatureFlagsOptions.FF_ENABLE_SPOT_SALES_MIRROR]: import.meta.env
          .REACT_APP_FF_ENABLE_SPOT_SALES_MIRROR,
        // A sub-flag da jornada mobile. Ela entra aqui para
        // serem ligáveis em dev local; o build de stg/prd NÃO injeta estas
        // vars, e é só isso que as mantém decidíveis por coorte no PostHog
        // sem deploy — a var de ambiente, se existir, ganha do PostHog.
        [FeatureFlagsOptions.FF_ENABLE_ESPELHO_NATIVO]: import.meta.env
          .REACT_APP_FF_ENABLE_ESPELHO_NATIVO,
        // Sem esta entrada a Central de Manutencao e INALCANCAVEL em dev: a
        // rota /central-manutencao redireciona para /home quando a flag e
        // falsa, e sem key do PostHog (o caso do dev local) getFlagValue
        // devolve false para ela sempre. Mesmo motivo do espelho de vendas
        // acima. Em stg/prd a var nao e injetada, entao a decisao continua
        // vindo do PostHog e nada muda.
        [FeatureFlagsOptions.FF_MANUTENCAO]: import.meta.env.REACT_APP_FF_MANUTENCAO,
        // Mesmo motivo da FF_MANUTENCAO acima: sem key do PostHog (dev local)
        // a aba de agentes seria inalcançável para quem não é Admin no banco
        // de desenvolvimento. Em stg/prd a var não é injetada, então a decisão
        // continua vindo do PostHog.
        [FeatureFlagsOptions.FF_MANUTENCAO_AGENTES_SEAZONER]: import.meta.env
          .REACT_APP_FF_MANUTENCAO_AGENTES_SEAZONER,
        // Ligável em dev local; em stg/prd a var não existe e decide o PostHog.
        [FeatureFlagsOptions.FF_PARCEIRO_FECHAR]: import.meta.env.REACT_APP_FF_PARCEIRO_FECHAR,
        // Ligável em dev local; em stg/prd a var não existe e decide o PostHog.
        [FeatureFlagsOptions.FF_WALLET_GRANDES_OPERACOES]: import.meta.env
          .REACT_APP_FF_WALLET_GRANDES_OPERACOES,
      };

      const getFlagValue = (key: FeatureFlagsOptions): boolean => {
        const override = overridesLocais[key];
        if (override !== undefined) {
          return override === 'true';
        }

        if (flagsDisabled.includes(key)) {
          return false;
        }

        if (typeof posthog.getFeatureFlag !== 'function') {
          return false;
        }
        return !!posthog.getFeatureFlag(key);
      };

      // Obtém os valores das flags (reduce tipado)
      const flags = flagKeys.reduce<FeatureFlagsProps>(
        (acc, key) => ({ ...acc, [key]: getFlagValue(key) }),
        {} as FeatureFlagsProps,
      );

      setFeatureFlags(flags);
      setLoading(false);
    };

    // Registra callback para quando as flags forem carregadas/atualizadas
    // Em posthog-js >= 1.250, onFeatureFlags retorna uma função de unsubscribe
    const maybeUnsubscribe = posthog.onFeatureFlags?.(() => {
      fetchFeatureFlags();
    });

    // Força reload das flags do servidor para evitar valores stale do cache local
    if (typeof posthog.reloadFeatureFlags === 'function') {
      posthog.reloadFeatureFlags();
    }

    // Sem key do PostHog (ex.: ambiente local), o callback `onFeatureFlags`
    // nunca dispara e as flags ficariam travadas em tudo-`false`, ignorando os
    // overrides por env. Aplica a resolução determinística uma vez de imediato;
    // com key, o callback do PostHog roda em seguida e refina os valores.
    if (!import.meta.env.REACT_APP_PUBLIC_POSTHOG_KEY) {
      fetchFeatureFlags();
    }

    return () => {
      cancelled = true;
      if (typeof maybeUnsubscribe === 'function') {
        maybeUnsubscribe();
      }
    };
  }, []);

  const value = useMemo(
    () => ({
      featureFlags,
      loading,
      setFeatureFlags,
    }),
    [featureFlags, loading, setFeatureFlags],
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};
